// src/components/ui/ErrorBoundary.jsx
//
// React error boundary with two fallback shapes:
//   variant="page"  (default) — full-page dark fallback, used by the root
//                    boundary in App.js. Something went wrong / Reload /
//                    Back to dashboard.
//   variant="panel" — compact inline card for a single dashboard section
//                    ("Couldn't load this section" + Retry). Retry resets
//                    THIS boundary's state — no full-page reload — so a
//                    single flaky panel doesn't take out its neighbours.
//
// Class component because getDerivedStateFromError / componentDidCatch
// have no hook equivalents.

import React from "react";
import { AlertTriangle, RotateCw, Home } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const label = this.props.label ? ` [${this.props.label}]` : "";
    console.error(`[ErrorBoundary${label}] render crash:`, error, info);
  }

  handleReload = () => window.location.reload();
  handleHome   = () => { window.location.href = "/messenger/profile"; };
  handleRetry  = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    // Compact panel-level fallback — one section failed, the rest of the
    // dashboard stays functional.
    if (this.props.variant === "panel") {
      return (
        <div style={{
          background: "rgba(226,75,74,0.06)",
          border: "0.5px solid rgba(226,75,74,0.25)",
          borderRadius: 12,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(226,75,74,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#E24B4A", flexShrink: 0,
          }}>
            <AlertTriangle size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>
              Couldn't load this section
            </div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
              {this.props.label ? `${this.props.label} · ` : ""}Other sections should still work.
            </div>
          </div>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px",
              background: "transparent",
              border: "0.5px solid rgba(226,75,74,0.4)",
              borderRadius: 8, color: "#E24B4A",
              fontSize: 12, cursor: "pointer",
              transition: "background 0.15s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(226,75,74,0.08)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
            <RotateCw size={11} /> Retry
          </button>
        </div>
      );
    }

    return (
      <div style={{
        minHeight: "100vh",
        background: "#050505",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}>
        <div style={{
          background: "#0a0a0a",
          border: "0.5px solid #1a1a1a",
          borderRadius: 14,
          padding: "32px 36px",
          maxWidth: 460,
          width: "100%",
          textAlign: "center",
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "rgba(226,75,74,0.1)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#E24B4A", marginBottom: 18,
          }}>
            <AlertTriangle size={26} />
          </div>
          <div style={{ fontSize: 16, color: "#fff", fontWeight: 500, marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6, marginBottom: 22 }}>
            The page hit an unexpected error and couldn't finish loading.
            Try reloading — if it keeps happening, head back to the dashboard.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "9px 18px",
                background: "transparent",
                border: "0.5px solid rgba(0,255,153,0.35)",
                borderRadius: 8, color: "#00FF99",
                fontSize: 13, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,255,153,0.08)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <RotateCw size={13} /> Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "9px 18px",
                background: "transparent",
                border: "0.5px solid #1e1e1e",
                borderRadius: 8, color: "#ccc",
                fontSize: 13, cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#0f0f0f"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <Home size={13} /> Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
